import { take, call, put, race, all, takeEvery, fork, select } from "redux-saga/effects";
import { actionCreators as truckActions } from './truckStore';
import * as Trucks from './truckStore';
import { TruckModel, getInitialModelValue } from "../TruckModels";
import * as api from '../../api/trucksApi';
import * as routeApi from "../../api/routesApi";
import { actions as rrfActions } from 'react-redux-form';
import { FilterInfo, RouteModel, LocationModel } from "../../common/models/CommonModels";
import { ApplicationState } from "../../store";
import * as Notifications from '../../notifications/notificationsStore';
import { sendTruckInformationEmailEvent, sendTruckInformationToReporterEmailEvent } from '../../utils/gaUtils';
import { localizations } from "../../localization/localization";

export function* watchTruckSagas() {
    yield all([
        takeEvery(Trucks.ActionType.RequestFreeTrucks, fetchFreeTrucks),
        takeEvery(Trucks.ActionType.AddNew, addNewFreeTruck),
        takeEvery(Trucks.ActionType.Delete, deleteTruckFlow),
        takeEvery(Trucks.ActionType.Copy, copyTruckFlow),
        takeEvery(Trucks.ActionType.SendEmail, sendEmailFlow),
        takeEvery(Trucks.ActionType.SendEmailToReporter, sendEmailToReporterFlow)
    ])
};

/**
 * Fetch free trucks and routes.
 */
export function* fetchFreeTrucks(action: Trucks.RequestFreeTrucksAction) {

    // Fetch trucks    
    const truckList: TruckModel[] = yield call(loadTrucks, action.filters);

    if (action.includeRoutes && truckList && truckList.length > 0) {
        // Fetch routes for trucks
        const trucksGrouping = new Map<string, TruckModel[]>();
        truckList.forEach(truck => {
            if (truck.destinationCity) {
                const trucksGroup = trucksGrouping.get(truck.departureCity.id + truck.destinationCity.id);
                if (!trucksGroup) {
                    trucksGrouping.set(truck.departureCity.id + truck.destinationCity.id, [truck]);
                }
                else {
                    trucksGroup.push(truck);
                }
            }
        })

        yield all(Array.from(trucksGrouping.values()).map(trucks => fetchTrucksRoute(trucks, trucks[0].departureCity.id, trucks[0].destinationCity!.id)));
    }
}

const getUser = (state: ApplicationState) => state.login;

function* loadTrucks(filters: FilterInfo) {
    const truckList: TruckModel[] = yield call(api.getTrucks, filters);

    const { userId, companyId } = yield select(getUser);

    let truckListWithUser = truckList.map(truck => Object.assign({}, truck, { isOwn: userId === truck.createdBy, isOwnCompany: companyId === truck.company.id }))
    if (filters.filterMode === "own") {
        truckListWithUser = truckListWithUser.filter(item => item.isOwn);
    }
    else if (filters.filterMode === "company") {
        truckListWithUser = truckListWithUser.filter(item => item.isOwnCompany);
    }
    else {
        truckListWithUser = truckListWithUser.filter(item => !item.deletedAt);
    }

    yield put(truckActions.receivedFreeTrucks(truckListWithUser))
    return truckListWithUser;
}

function* fetchTrucksRoute(trucks: TruckModel[], fromId: string, toId: string) {
    const routeResult: routeApi.RouteResult | undefined = yield call(routeApi.getRoute, fromId, toId);

    if (routeResult) {
        const routes: RouteModel[] = trucks.map(t => routeApi.getRouteModel(t.id, t.departureCity, t.destinationCity!, routeResult));
        yield put(truckActions.receivedTruckRoutes(routes));
    }
}

function* truckCopyFailed() {
    yield put(Notifications.actionCreators.addNotification({ id: "truckCopyFailed", message: localizations.validation.truckCopyError, variant: "error", autohide: true  }));
}
function* truckAddingFailed() {
    yield put(Notifications.actionCreators.addNotification({ id: "truckCopyFailed", message: localizations.validation.truckAddingError, variant: "error", autohide: true  }));
}

function* addNewFreeTruck(action: Trucks.AddOrEditTruckAction): IterableIterator<any> {

    let editMode = false;
    if (action.truck) {
        yield put(rrfActions.load("newTruck", action.truck));
        editMode = true;
    }

    // This action will inform the UI to show add dialog.
    yield put(truckActions.addOrEditTruckStarted(action.truck));

    const { submit, cancel } : any = yield race({
        submit: take(Trucks.ActionType.FormSubmitted),
        cancel: take(Trucks.ActionType.FormCancelled)
    });

    if (submit) {
        try {
            // If truck is not selected try to add a new one. Otherwise edit currently selected.
            if (editMode) {
                const succeeded = yield call(api.editTruck, submit.data);
                if (succeeded) { 
                    const routeResult: routeApi.RouteResult | undefined = yield call(routeApi.getRoute, submit.data.departureCity.id, submit.data.destinationCity.id);
                    if (routeResult) {
                        const route = routeApi.getRouteModel(submit.data.id, submit.data.departureCity, submit.data.destinationCity, routeResult);
                        yield put(truckActions.receivedTruckRoutes([route]));
                    }
                }
                else {
                    yield put({ type: "COMMON/REQUEST_ERROR", error: { error: "Failed to update truck" } });
                    yield fork(truckAddingFailed);
                }
            }
            else {
                const succeeded = yield call(api.addTruck, submit.data);
                if (succeeded) {
                    const routeResult: routeApi.RouteResult | undefined = yield call(routeApi.getRoute, submit.data.departureCity.id, submit.data.destinationCity.id);
                    if (routeResult !== undefined) {
                        const route = routeApi.getRouteModel(submit.data.id, submit.data.departureCity, submit.data.destinationCity, routeResult);
                        yield put(truckActions.receivedTruckRoutes([route]));
                    }
                }
                else {
                    yield put({ type: "COMMON/REQUEST_ERROR", error: { error: "Failed to create truck" } });
                    yield fork(truckAddingFailed);
                }
            }
        }
        catch (error) {
            yield put({ type: "COMMON/REQUEST_ERROR", error: { error } });
            yield fork(truckAddingFailed);
        }
    }
    else {
        console.log("form cancelled");
        yield put(truckActions.addOrEditTruckCancelled());
    }

    yield put(truckActions.addOrEditTruckSuccess());
    yield put(rrfActions.load("newTruck", getInitialModelValue()));

    if (action.afterAddOrEdit) {
        action.afterAddOrEdit();
    }

    // HACK: Reload trucks using last used filters.
    if (action.reloadTrucks) {
        const filters = yield select(getFilters);
        yield call(loadTrucks, filters);
    }
}

const getFilters = (state: ApplicationState) => state.trucks.filters;

function* deleteTruckFlow(action: Trucks.DeleteAction) {

    // Inform UI to show the dialog.
    yield put(truckActions.deleteStarted(action.truck));

    // Wait for appropriate data.
    const winner = yield race({
        submit: take(Trucks.ActionType.DeleteSubmitted),
        cancel: take(Trucks.ActionType.DeleteCancelled)
    });

    // If submit was called, call delete resource api
    if (winner.submit) {
        try {
            const inject = Object.assign({}, winner.submit.data, { id: action.truck.id })

            yield call(api.deleteTruck, inject);
        } catch (error) {
            yield put(truckActions.deleteError(error))
        }
    }

    yield put(truckActions.deleteSuccess());
    yield put(rrfActions.reset("deleteTruck"))

    if (action.afterDelete) {
        action.afterDelete();
    }

    // HACK: Reload trucks using last used filters.
    const filters = yield select(getFilters);
    yield call(loadTrucks, filters);
}

function* copyTruckFlow(action: Trucks.CopyAction) {
    // Create copy without id.
    const copy = Object.assign({}, action.truck, { id: "" });

    yield put(rrfActions.load("newTruck", copy));

    yield put(truckActions.copyStarted(copy));

    const { submit } = yield race({
        submit: take(Trucks.ActionType.FormSubmitted),
        cancel: take(Trucks.ActionType.FormCancelled)
    });

    if (submit) {
        try {
            const response = yield call(api.addTruck, submit.data)
        }
        catch (error) {
            yield put({ type: "COMMON/REQUEST_ERROR", error: { error } });
            yield fork(truckCopyFailed);
        }
    }

    yield put(truckActions.copySuccess());
    yield put(rrfActions.load("newTruck", getInitialModelValue()));

    // HACK: Reload trucks using last used filters.
    const filters = yield select(getFilters);
    yield call(loadTrucks, filters);
}

function* sendEmailFlow(action: Trucks.SendEmailAction) {
    // Inform UI to show email dialog.
    yield put(Trucks.actionCreators.sendEmailStarted(action.truck));
    const { submit } = yield race({
        submit: take(Trucks.ActionType.SendEmailSubmitted),
        cancel: take(Trucks.ActionType.SendEmailCancelled)
    });
    if (submit) {
        try {
            const succeeded = yield call(api.sendEmail, action.truck.id, submit.data.message, submit.data.emailAddress);
            if (succeeded) {
                yield put(Trucks.actionCreators.sendEmailSuccess());
                yield put(rrfActions.reset("infoEmail"));
                sendTruckInformationEmailEvent();
            } else {
                yield put(Trucks.actionCreators.sendEmailError());
            }
        }
        catch (error) {
            yield put({ type: "COMMON/REQUEST_ERROR", error: { error } });
        }
    }
    else {
        yield put(Trucks.actionCreators.sendEmailCancelled());
        yield put(rrfActions.reset("infoEmail"));
    }
}

function* sendEmailToReporterFlow(action: Trucks.SendEmailToReporterAction) {
    // Inform UI to show email dialog.
    yield put(Trucks.actionCreators.sendEmailToReporterStarted(action.truck));
    const { submit } = yield race({
        submit: take(Trucks.ActionType.SendEmailToReporterSubmitted),
        cancel: take(Trucks.ActionType.SendEmailToReporterCancelled)
    });

    if (submit) {
        try {
            const succeeded = yield call(api.sendEmailToReporter, action.truck.id, submit.data.message);
            if (succeeded) {
                yield put(Trucks.actionCreators.sendEmailToReporterSuccess());
                yield put(rrfActions.reset("infoEmail"));
                sendTruckInformationToReporterEmailEvent();
            } else {
                yield put(Trucks.actionCreators.sendEmailToReporterError());
            }
        }
        catch (error) {
            yield put({ type: "COMMON/REQUEST_ERROR", error: { error } });
        }
    }
    else {
        yield put(Trucks.actionCreators.sendEmailToReporterCancelled());
        yield put(rrfActions.reset("infoEmail"));
    }
}



// WEBPACK FOOTER //
// ./src/trucks/store/sagas.ts